"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useRouter, usePathname } from "next/navigation";
import useMediaQuery from "@/utils/useMediaQuery";
import Link from "next/link";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { academicsMegaMenuData } from "../../utils/academicsMenuData";
import dynamic from "next/dynamic";
import "./Header.css";

const MegaMenu = dynamic(
  () => import("./MegaMenu").then((module) => module.MegaMenu),
  // { ssr: false },
);

const slideVariants = {
  initial: (direction) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0,
  }),
  animate: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? "-100%" : "100%",
    opacity: 0,
  }),
};

const SubMenuScreen = ({
  title,
  items,
  parent,
  onBack,
  onDrilldown,
  closeMenu,
  renderMegaMobileMenuItems,
}) => (
  <div className="w-full">
    <div className="flex items-center px-6 py-3 border-b border-gray-200 bg-white">
      <button onClick={onBack} className="mr-4" aria-label="Go back">
        <ArrowLeft className="h-6 w-6 text-gray-900" />
      </button>

      <div className="flex flex-1 items-center justify-between">
        <span className="font-bold text-[#0475B3] text-base">{title}</span>
        {(() => {
          const parentUrl = parent?.url;
          if (parentUrl && parentUrl !== "#" && parentUrl !== "/") {
            return (
              <Link
                href={parentUrl}
                className="flex items-center gap-1 text-sm font-semibold text-[#FC9D00]"
                 prefetch={false} 
                onClick={closeMenu}
              >
                Explore
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            );
          }
          return null;
        })()}
      </div>
    </div>
    <nav>{renderMegaMobileMenuItems(items)}</nav>
  </div>
);

const ServerUrl = process.env.NEXT_PUBLIC_SERVER_URL;

const Page = ({ menuData, isWise }) => {
  const router = useRouter();
  const pathname = usePathname();

  // Helper function to process menu data
  const processMenuData = (data) => {
    const menuSlug = "header-menu";
    const topHeaderSlug = "top-header";

    const header_menu = data?.data?.find((menu) => menu.slug === menuSlug);
    const top_header_menu = data?.data?.find(
      (menu) => menu.slug === topHeaderSlug,
    );

    const items = Array.isArray(header_menu?.items) ? header_menu.items : [];

    let topLinks = [];
    if (Array.isArray(top_header_menu?.items)) {
      topLinks = top_header_menu.items.map((item) => ({
        id: item.id,
        label: item.title,
        href: item.url || "#",
        target: item.target || "_self",
      }));
    }

    return { items, topLinks };
  };

  // Initialize state from props if available
  const initial = menuData
    ? processMenuData(menuData)
    : { items: [], topLinks: [] };

  const [menuItems, setMenuItems] = useState(initial.items);
  const [topHeaderLinks, setTopHeaderLinks] = useState(initial.topLinks);
  const [activeMenuMain, setActiveMenuMain] = useState(null);
  const [activeMenuSticky, setActiveMenuSticky] = useState(null);
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [submenuStack, setSubmenuStack] = useState([]);
  const [mobileExpanded, setMobileExpanded] = useState({});

  const [showHeader, setShowHeader] = useState(true);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward
  // Tailwind lg breakpoint. The sticky header and its listeners are desktop
  // only, so don't render/attach them on phones (false on server, like before).
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const mouseInsideMega = useRef(false);

  const lastScrollY = useRef(0);
  const lastHideY = useRef(0);

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setSubmenuStack([]);
    setMobileExpanded({});
  };
  // Close desktop menus whenever route changes
  const closeDesktopMenus = () => {
    setActiveMenuMain(null);
    setActiveMenuSticky(null);
    setActiveSectionIdx(0);
  };
  useEffect(() => {
    setActiveMenuMain(null);
    setActiveMenuSticky(null);
    setActiveSectionIdx(0);
  }, [pathname]);

  // Close mobile menu if we switch to desktop width
  useEffect(() => {
    const handleResize = () => {
      // Tailwind lg breakpoint = 1024px
      if (window.innerWidth >= 1024 && mobileOpen) {
        closeMobileMenu();
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [mobileOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Sticky header show/hide logic
  useEffect(() => {
    if (!isDesktop) return;
    const showOffset = 200;
    const revealDistance = 60;

    const handleScroll = () => {
      const currentY = window.scrollY;

      // If mobile menu is open, don't show sticky header
      if (mobileOpen) {
        setShowStickyHeader(false);
        lastScrollY.current = currentY;
        lastHideY.current = currentY;
        return;
      }

      // Before we cross the offset, do not show sticky header at all
      if (currentY < showOffset) {
        setShowStickyHeader(false);
        lastScrollY.current = currentY;
        lastHideY.current = currentY;
        return;
      }

      if (currentY > lastScrollY.current) {
        // scrolling DOWN
        setShowStickyHeader(true);
        lastHideY.current = currentY;
      } else {
        // scrolling UP
        if (lastHideY.current - currentY > revealDistance) {
          setShowStickyHeader(true);
        }
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mobileOpen, isDesktop]);

  // Measure header height so we can insert a spacer when header becomes fixed
  useEffect(() => {
    const measure = () => {
      try {
        const h = headerRef.current?.offsetHeight || 0;
        setHeaderHeight(h);
      } catch (e) {
        setHeaderHeight(0);
      }
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Only listen while a desktop dropdown is open. Attached permanently, the
  // non-passive document touchmove listener and the mouseover layout reads
  // (scrollHeight) ran on every tap/scroll on phones.
  const desktopMenuOpen = activeMenuMain !== null || activeMenuSticky !== null;
  useEffect(() => {
    if (!desktopMenuOpen) return;
    const checkAndConstrainInner = () => {
      try {
        const inner = document.querySelector(
          ".megamenu-submenu.active > .mx-auto",
        );
        const buffer = 88; // space for header/tops
        const maxAllowed = window.innerHeight - buffer;

        if (inner) {
          if (inner.scrollHeight > maxAllowed) {
            inner.style.maxHeight = `${maxAllowed}px`;
            inner.style.overflowY = "auto";
            inner.style.overscrollBehavior = "contain";
          } else {
            inner.style.maxHeight = "";
            inner.style.overflowY = "";
            inner.style.overscrollBehavior = "";
          }
        }
      } catch (e) {
        // ignore
      }
    };

    const onOver = (e) => {
      const inside = !!(
        e.target &&
        e.target.closest &&
        e.target.closest(".megamenu-submenu.active")
      );
      mouseInsideMega.current = inside;
      checkAndConstrainInner();
    };

    const onOut = (e) => {
      const inside = !!(
        e.relatedTarget &&
        e.relatedTarget.closest &&
        e.relatedTarget.closest(".megamenu-submenu.active")
      );
      mouseInsideMega.current = !!inside;
      checkAndConstrainInner();
    };

    const onWheel = (e) => {
      if (!mouseInsideMega.current) return;
      const inner = document.querySelector(
        ".megamenu-submenu.active > .mx-auto",
      );
      if (!inner) return;
      // If the wheel event was triggered inside the inner container, allow it
      if (
        e.target &&
        e.target.closest &&
        e.target.closest(".megamenu-submenu.active > .mx-auto")
      )
        return;
      e.preventDefault();
    };

    const onTouchMove = (e) => {
      if (!mouseInsideMega.current) return;
      const inner = document.querySelector(
        ".megamenu-submenu.active > .mx-auto",
      );
      if (!inner) return;
      if (
        e.target &&
        e.target.closest &&
        e.target.closest(".megamenu-submenu.active > .mx-auto")
      )
        return;
      e.preventDefault();
    };

    checkAndConstrainInner();
    window.addEventListener("resize", checkAndConstrainInner);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    // capture listeners and passive:false so we can preventDefault
    document.addEventListener("wheel", onWheel, {
      passive: false,
      capture: true,
    });
    document.addEventListener("touchmove", onTouchMove, {
      passive: false,
      capture: true,
    });

    return () => {
      mouseInsideMega.current = false;
      window.removeEventListener("resize", checkAndConstrainInner);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("wheel", onWheel, { capture: true });
      document.removeEventListener("touchmove", onTouchMove, { capture: true });
    };
  }, [desktopMenuOpen, activeMenuMain, activeMenuSticky, headerHeight]);

  // Fetch header-menu + top-header-menu
  useEffect(() => {
    if (menuData) return;

    const fetchMenuData = async () => {
      try {
        const response = await fetch(`${ServerUrl}/tree-menus/menu/`);
        const json = await response.json();

        const { items, topLinks } = processMenuData(json);
        setMenuItems(items);
        setTopHeaderLinks(topLinks);
      } catch (error) {
        console.error("Error fetching menu data:", error);
      }
    };

    fetchMenuData();
  }, [menuData]);

  // Helper to check mega menu class
  const isMegaMenu = (item) =>
    item.class && item.class.includes("mega-menu-container");

  // Get the children for rendering
  const getMenuChildren = (item) => {
    if (isMegaMenu(item)) return academicsMegaMenuData;
    return item.children;
  };

  // Desktop Menu
  const renderMenuItems = (items, depth = 0, menuType = "main") =>
    items.map((item) => {
      const hasMega = isMegaMenu(item);
      const children = getMenuChildren(item);
      const hasChildren = Array.isArray(children) && children.length > 0;

      const isSticky = menuType === "sticky";
      const activeMenu = isSticky ? activeMenuSticky : activeMenuMain;
      const setActiveMenu = isSticky ? setActiveMenuSticky : setActiveMenuMain;

      const isActive = activeMenu === item.id;

      const relTarget =
        item.target === "_blank" ? "noopener noreferrer" : undefined;
      const relNoFollow = item.noFollow == true ? "nofollow" : undefined;

      const relValue =
        [relTarget, relNoFollow].filter(Boolean).join(" ") || undefined;
      return (
        <li
          key={item.id}
          className={`main-menu-item-wrap relative group lg:mr-5 xl:mr-8 ${item.hide == true ? "hide-title" : ""}`}
          onMouseEnter={() => {
            setActiveMenu(item.id);
            if (hasMega) setActiveSectionIdx(0);
          }}
          onMouseLeave={(e) => {
            const toEl = e.relatedTarget;
            const isInside =
              toEl && toEl instanceof Node && e.currentTarget.contains(toEl);
            if (isInside) return;
            setActiveMenu(null);
          }}
        >
          {hasChildren ? (
            <div
              className={`main-menu-item cursor-pointer flex items-center py-2 text-gray-800 hover:text-[#0B7EAE]`}
              aria-expanded={isActive}
            >
              {item.title}

              {hasChildren && (
                <span className="absolute ml-1 lg:text-lg xl:text-xl menu-icon transition-all duration-300 mr-2">
                  <span
                    className={`absolute block transition-all duration-300 plus-icon ${
                      isActive
                        ? "scale-y-0 opacity-0"
                        : "scale-y-100 opacity-100"
                    } origin-center`}
                  >
                    +
                  </span>
                  <span
                    className={`absolute block transition-all duration-300 minus-icon ${
                      isActive
                        ? "scale-y-100 opacity-100"
                        : "scale-y-0 opacity-0"
                    } origin-center -mt-[1em]`}
                  >
                    −
                  </span>
                </span>
              )}
            </div>
          ) : (
            <Link
              href={item.url || "#"}
              target={item.target || "_self"}
              rel={relValue}
              className="main-menu-item cursor-pointer flex items-center py-2 text-gray-800 hover:text-[#0B7EAE]"
              aria-expanded={isActive}
               prefetch={false} 
              onClick={() => {
                if (item.target !== "_blank") {
                  closeDesktopMenus();
                }
              }}
            >
              {item.title}
            </Link>
          )}

          {/* Desktop Mega Menu */}
          {hasChildren && hasMega &&  (
            <MegaMenu
              isActive={isActive}
              activeSectionIdx={activeSectionIdx}
              setActiveSectionIdx={setActiveSectionIdx}
              closeDesktopMenus={closeDesktopMenus}
            >
              {children}
            </MegaMenu>
          )}

          {/* Regular Dropdown */}
          {hasChildren && !hasMega && (
            <ul
              className={`submenu absolute w-75 bg-white shadow-lg z-30 ${
                item.class || ""
              } ${isActive ? "active" : ""}`}
              onMouseEnter={() => setActiveMenu(item.id)}
              onMouseLeave={() => setActiveMenu(null)}
            >
              {renderMenuItems(children, depth + 1, menuType)}
            </ul>
          )}

          <span className="pointer-events-none absolute left-0 right-0 top-full h-2" />
        </li>
      );
    });

  // Mobile helpers
  const getCurrentMenu = () =>
    submenuStack.length === 0
      ? null
      : submenuStack[submenuStack.length - 1].children || [];

  const getCurrentTitle = () =>
    submenuStack.length === 0
      ? null
      : submenuStack[submenuStack.length - 1].title;

  const handleMegaMenuClick = (item) => {
    setDirection(1);
    const children = getMenuChildren(item);
    setSubmenuStack([{ ...item, children }]);
  };

  const handleDrilldownClick = (item) => {
    if (Array.isArray(item.children) && item.children.length > 0) {
      setDirection(1);
      setSubmenuStack([...submenuStack, item]);
    } else {
      router.push(item.url || "/");
      closeMobileMenu();
    }
  };

  const handleBack = () => {
    setDirection(-1);
    setSubmenuStack(submenuStack.slice(0, -1));
  };

  const handleExpandToggle = (id) => {
    setMobileExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderNormalMobileMenuItems = (items) => (
    <ul>
      {items.map((item) => {
        const hasChildren =
          Array.isArray(item.children) && item.children.length > 0;

        return (
          <li key={item.id ?? `${item.title}-${item.url}`} className="border-b border-gray-200">
            <div className="flex items-center justify-between py-4 px-6">
              <Link
                href={item.url || "#"}
                target={item.target || "_self"}
                className="text-base text-gray-900 font-medium flex-1"
                prefetch={false} 
                onClick={() => {
                  if (item.target !== "_blank") closeMobileMenu();
                }}
              >
                {item.title}
              </Link>

              {hasChildren && (
                <button
                  aria-label="Expand submenu"
                  className="ml-2 font-bold text-xl"
                  onClick={() => handleExpandToggle(item.id)}
                  style={{ background: "none", border: "none", padding: 0 }}
                >
                  {mobileExpanded[item.id] ? "−" : "+"}
                </button>
              )}
            </div>

            {hasChildren && mobileExpanded[item.id] && (
              <div className="bg-gray-50">
                {renderNormalMobileMenuItems(item.children)}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  const renderMegaMobileMenuItems = (items) => (
    <ul>
      {(items || []).map((item) => {
        const hasChildren =
          Array.isArray(item.children) && item.children.length > 0;

        return (
          <li key={item.id ?? `${item.title}-${item.url}`} className="border-b border-gray-200">
            <div className="flex items-center justify-between py-4 px-6">
              <button
                className="text-base text-gray-900 font-medium flex-1 text-left"
                onClick={() => handleDrilldownClick(item)}
                style={{ background: "none", border: "none", padding: 0 }}
              >
                {item.title}
              </button>

              {hasChildren && (
                <button
                  aria-label="Open submenu"
                  className="ml-2 font-normal text-xl"
                  onClick={() => handleDrilldownClick(item)}
                  style={{ background: "none", border: "none", padding: 0 }}
                >
                  +
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );

  // Shared desktop grid (logo + top links + main nav + CTA)
  const renderDesktopNav = (menuType = "main") => (
    <div className="hidden max-w-[var(--max-page-width)] px-[var(--container-padding)] mx-auto lg:grid grid-cols-[auto_1fr] grid-rows-2 items-center relative">
      <div className="row-span-2 flex items-center">
        <Link href="/">
          <Image
            src="/logo.svg"
            alt="Medhavi Skills University"
            width={218}
            height={72}
            className="lg:h-14 xl:h-16 2xl:h-18 w-auto"
            priority
          />
        </Link>
      </div>

      <div className="flex justify-end items-center py-0">
        <ul className="flex space-x-6">
          {topHeaderLinks.map((item) => (
            <li key={item.id || item.label}>
              <Link
                href={item.href}
                target={item.target || "_self"}
                 prefetch={false} 
                className="text-gray-600 hover:text-gray-800 text-sm"
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex justify-end">
        <nav className="flex justify-end items-center self-center">
          <ul
            className="flex space-x-4 lg:text-sm xl:text-base font-medium"
            onMouseLeave={() =>
              menuType === "main"
                ? setActiveMenuMain(null)
                : setActiveMenuSticky(null)
            }
          >
            {menuItems.length > 0 ? (
              renderMenuItems(menuItems, 0, menuType)
            ) : (
              <ul className="flex space-x-4 lg:text-sm xl:text-base font-medium mr-8">
                <li
                  tabIndex={0}
                  role="button"
                  aria-expanded="false"
                  className="main-menu-item cursor-pointer flex items-center py-2 text-gray-800 hover:text-[#0B7EAE]"
                >
                  About{" "}
                  <span aria-hidden="true" className="ml-1">
                    +
                  </span>
                </li>
                <li
                  tabIndex={0}
                  role="button"
                  aria-expanded="false"
                  className="main-menu-item cursor-pointer flex items-center py-2 text-gray-800 hover:text-[#0B7EAE]"
                >
                  Academics{" "}
                  <span aria-hidden="true" className="ml-1">
                    +
                  </span>
                </li>
                <li
                  tabIndex={0}
                  role="button"
                  aria-expanded="false"
                  className="main-menu-item cursor-pointer flex items-center py-2 text-gray-800 hover:text-[#0B7EAE]"
                >
                  Admissions{" "}
                  <span aria-hidden="true" className="ml-1">
                    +
                  </span>
                </li>
                <li
                  tabIndex={0}
                  role="button"
                  aria-expanded="false"
                  className="main-menu-item cursor-pointer flex items-center py-2 text-gray-800 hover:text-[#0B7EAE]"
                >
                  Life @MSU{" "}
                  <span aria-hidden="true" className="ml-1">
                    +
                  </span>
                </li>
                <li
                  tabIndex={0}
                  role="button"
                  aria-expanded="false"
                  className="main-menu-item cursor-pointer flex items-center py-2 text-gray-800 hover:text-[#0B7EAE]"
                >
                  Collaborations{" "}
                  <span aria-hidden="true" className="ml-1">
                    +
                  </span>
                </li>
                <li
                  tabIndex={0}
                  role="link"
                  className="main-menu-item cursor-pointer flex items-center py-2 text-gray-800 hover:text-[#0B7EAE]"
                >
                  Placements
                </li>
              </ul>
            )}
          </ul>
        </nav>

        <div className="flex items-center space-x-5">
          <Link
            href={
              isWise ? "https://apply.msu.edu.in/medhavi-wise" : "/application"
            }
            className="bg-[#081E2D] hover:bg-[#FC9D00] text-white hover:text-black transition-[.3s] font-medium px-6 py-3 mb-2"
            target={isWise ? "_blank" : ""}
          >
            <span id="btn-apply-now-top">Apply Now</span>
          </Link>
        </div>
      </div>
    </div>
  );

  // The desktop nav (incl. the large mega menu) is in the DOM on phones too,
  // just hidden by CSS. Memoize it so opening the mobile menu or drilling
  // into it doesn't re-render that whole hidden tree on every tap.
  const mainDesktopNav = useMemo(
    () => renderDesktopNav("main"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuItems, topHeaderLinks, activeMenuMain, activeSectionIdx, isWise],
  );
  const stickyDesktopNav = useMemo(
    () => (isDesktop ? renderDesktopNav("sticky") : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDesktop, menuItems, topHeaderLinks, activeMenuSticky, activeSectionIdx, isWise],
  );

  return (
    <>
      {/* Persistent spacer so content doesn't shift when header is fixed */}
      {/* {headerHeight > 0 && <div aria-hidden="true" style={{ height: headerHeight }} />} */}

      {/* Sticky Header (desktop only) */}
      {isDesktop && (
        <div
          className={`hidden sticky-custom lg:block fixed top-0 left-0 right-0 z-[999] bg-white shadow-md transition-transform duration-300 ${
            showStickyHeader ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          {stickyDesktopNav}
        </div>
      )}

      {/* Header is positioned absolute and shown/hidden via transform for smooth animation */}
      <header
        ref={headerRef}
        style={{ willChange: "transform" }}
        className={`w-full bg-white top-0 relative left-0 right-0 z-[1000] ${mobileOpen ? "h-full" : `transition-transform duration-300  ${showHeader ? "translate-y-0" : "-translate-y-full"}`}`}
      >
        {/* Top Notice - Ticker */}
        <div className="w-full bg-[#0E74AA] hidden md:block text-white text-xs lg:text-cs font-medium">
          <div className="max-w-[var(--max-page-width)] px-[var(--container-padding)] mx-auto">
            <div className="overflow-hidden">
              <div className="ticker relative">
                <div className="ticker-track flex items-center whitespace-nowrap">
                  <div className="ticker-group flex items-center space-x-12">
                    <a
                      className="ticker-item hover:underline"
                      href="https://application.msu.edu.in/campus-programme-registration"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Admissions 2026 Are Open. Enrol at India's Premier Skills
                      University
                    </a>
                    {/* <a className="ticker-item hover:underline" href="https://cdn.msu.edu.in/pdf/Public-Notice-Removal-of-MSU-Name.pdf" target="_blank" rel="noreferrer">Public Notice Removal of MSU Name</a> */}
                  </div>

                  <div
                    className="ticker-group flex items-center space-x-12"
                    aria-hidden="true"
                  >
                    <a
                      className="ticker-item hover:underline"
                      href="https://application.msu.edu.in/campus-programme-registration"
                      target="_blank"
                      rel="noreferrer"
                      tabIndex={-1}
                    >
                      Admissions 2026 Are Open. Enrol at India's Premier Skills
                      University
                    </a>
                  </div>

                  <div
                    className="ticker-group flex items-center space-x-12"
                    aria-hidden="true"
                  >
                    <a
                      className="ticker-item hover:underline"
                      href="https://application.msu.edu.in/campus-programme-registration"
                      target="_blank"
                      rel="noreferrer"
                      tabIndex={-1}
                    >
                      Admissions 2026 Are Open. Enrol at India's Premier Skills
                      University
                    </a>
                  </div>

                  <div
                    className="ticker-group flex items-center space-x-12"
                    aria-hidden="true"
                  >
                    <a
                      className="ticker-item hover:underline"
                      href="https://application.msu.edu.in/campus-programme-registration"
                      target="_blank"
                      rel="noreferrer"
                      tabIndex={-1}
                    >
                      Admissions 2026 Are Open. Enrol at India's Premier Skills
                      University
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <style>{`\n          .ticker { height: 40px; display: flex; align-items: center; }\n          .ticker-track { display: flex; gap: 4rem; align-items: center; animation: marquee 50s linear infinite; }\n          .ticker-item { color: #ffffff; font-size: 0.9rem; }\n          .ticker a { color: #ffffff; }\n          .ticker-track:hover { animation-play-state: paused; }\n\n          @keyframes marquee {\n            0% { transform: translateX(0); }\n            100% { transform: translateX(-50%); }\n          }\n\n          @media (min-width: 1024px) {\n            .ticker { height: 34px; }\n            .ticker-item { font-size: 0.9rem; }\n          }\n        `}</style>
        </div>

        {/* Desktop Navigation */}
        {mainDesktopNav}

        {/* Mobile & Tablet Navbar */}
        <div className="lg:hidden max-w-[var(--max-page-width)] px-[var(--container-padding)] mx-auto py-3 flex items-center justify-between">
          <Link href="/">
            <Image
            src="/logo.svg"
            alt="Medhavi Skills University"
            width={218}
            height={72}
            className="lg:h-14 xl:h-16 2xl:h-18 w-auto"
            priority
          />
          </Link>

          <div className="flex items-center space-x-4">
            {/* <button aria-label="Search" className="p-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-4.35-4.35M18 11A7 7 0 114 11a7 7 0 0114 0z"
              />
            </svg>
          </button> */}

            {/* <Link
              href={
                isWise
                  ? "https://apply.msu.edu.in/medhavi-wise"
                  : "/application"
              }
              target={isWise ? "_blank" : ""}
              className="bg-[#081E2D] hover:bg-[#FC9D00] text-white hover:text-black transition-[.3s] font-bold px-3 py-2 text-xs"
            >
              <span id="btn-apply-now-sticky-header">Apply Now</span>
            </Link> */}

            <button
              aria-label="Open menu"
              className="p-0"
              onClick={() => setMobileOpen(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-7 w-7"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M3 6h18" />
                <path d="M8 12h12" />
                <path d="M3 18h18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence initial={false}>
          {mobileOpen && (
            <motion.div
              key="mobile-menu-overlay"
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
              className="fixed inset-0 bg-white overflow-y-auto min-h-screen"
              style={{ willChange: "opacity, transform", backfaceVisibility: "hidden" }}
            >
              <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 bg-white">
                <Link href="/" onClick={closeMobileMenu}>
                  <Image
                    src="/logo.svg"
                    alt="Medhavi Skills University"
                    width={218}
                    height={72}
                    className="lg:h-14 xl:h-16 2xl:h-18 w-auto"
                    priority
                  />
                </Link>

                <button
                  aria-label="Close menu"
                  className="p-2"
                  onClick={closeMobileMenu}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-gray-900"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="relative min-h-[calc(100vh-60px)] w-full overflow-hidden">
                <AnimatePresence
                  mode="wait"
                  initial={false}
                  custom={direction}
                >
                {submenuStack.length === 0 ? (
                  <motion.div
                    key="main-menu"
                    custom={direction}
                    variants={slideVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
                    className="w-full absolute left-0 top-0"
                  >
                    <nav>
                      <ul>
                        {menuItems.map((item) => {
                          if (isMegaMenu(item)) {
                            return (
                              <li
                                key={item.id ?? `${item.title}-${item.url}`}
                                className="border-b border-gray-200"
                              >
                                <div className="flex items-center justify-between py-4 px-6">
                                  <button
                                    className="text-base text-black font-normal flex-1 text-left"
                                    onClick={() => handleMegaMenuClick(item)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                    }}
                                  >
                                    {item.title}
                                  </button>
                                  <span className="ml-2 font-normal text-xl text-black cursor-pointer">
                                    +
                                  </span>
                                </div>
                              </li>
                            );
                          }

                          const hasChildren =
                            Array.isArray(item.children) &&
                            item.children.length > 0;

                          return (
                            <li
                              key={item.id ?? `${item.title}-${item.url}`}
                              className="border-b border-gray-200"
                            >
                              <div className="flex items-center justify-between py-4 px-6">
                                {hasChildren ? (
                                  <button
                                    type="button"
                                    aria-label="Expand submenu"
                                    onClick={() => handleExpandToggle(item.id)}
                                    className="flex items-center justify-between w-full"
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                    }}
                                  >
                                    <span className="text-base text-gray-900 font-medium flex-1 text-left">
                                      {item.title}
                                    </span>
                                    <span className="ml-2 font-medium text-xl">
                                      {mobileExpanded[item.id] ? "−" : "+"}
                                    </span>
                                  </button>
                                ) : (
                                  <Link
                                    href={item.url || "#"}
                                    target={item.target || "_self"}
                                    className="text-base text-gray-900 font-medium flex-1"
                                     prefetch={false} 
                                    onClick={() => {
                                      if (item.target !== "_blank")
                                        closeMobileMenu();
                                    }}
                                  >
                                    {item.title}
                                  </Link>
                                )}
                              </div>
                              {hasChildren && mobileExpanded[item.id] && (
                                <div className="bg-gray-50">
                                  {renderNormalMobileMenuItems(item.children)}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </nav>
                    <ul className="mt-4 px-6 space-y-3">
                      {topHeaderLinks.map((item) => (
                        <li key={item.id || item.label}>
                          <Link
                            href={item.href}
                            target={item.target || "_self"}
                            className="text-gray-700 text-base"
                             prefetch={false} 
                            onClick={() => {
                              if (item.target !== "_blank") closeMobileMenu();
                            }}
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <div className="pb-8" />
                  </motion.div>
                ) : (
                  <motion.div
                    key={`submenu-${submenuStack.length}`}
                    custom={direction}
                    variants={slideVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
                    className="w-full absolute left-0 top-0"
                  >
                    <SubMenuScreen
                      title={getCurrentTitle()}
                      items={getCurrentMenu()}
                      parent={submenuStack[submenuStack.length - 1]}
                      onBack={handleBack}
                      closeMenu={closeMobileMenu}
                      renderMegaMobileMenuItems={renderMegaMobileMenuItems}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </header>
    </>
  );
};

export default Page;
